import {
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    inject,
    input,
    linkedSignal,
    signal,
    untracked,
    viewChild,
} from "@angular/core";
import { TypeManagerDecorator } from "../../../../main/type.map.service";
import { Table } from "../../../s25-table/Table";
import { S25TableModule } from "../../../s25-table/s25.table.module";
import { S25TableComponent } from "../../../s25-table/s25.table.component";
import { GenericTableSimpleSelectComponent } from "../../../s25-table/generics/generic.table.simple.select.component";
import { S25DatepickerComponent } from "../../../s25-datepicker/s25.datepicker.component";
import { S25DatepickerModule } from "../../../s25-datepicker/s25.datepicker.module";
import { S25CollapseChevronComponent } from "../../../s25-collapse-chevron/s25.collapse.chevron.component";
import { S25FadingSuccessMsgComponent } from "../../../s25-fading-success-msg/s25.fading.success.msg.component";
import { S25ButtonComponent } from "../../../../standalone/s25.button.component";
import { PaymentsStore } from "./s25.payments.store";
import { PaymentRows } from "./payment-rows";
import { S25PaymentActionCellComponent } from "./s25.payment.action.cell.component";
import { S25PaymentAmountFieldComponent } from "./s25.payment.amount.field.component";
import { S25PaymentTextFieldComponent } from "./s25.payment.text.field.component";
import { S25PaymentNotesCellComponent } from "./s25.payment.notes.cell.component";
import { S25VoidPaymentFormComponent } from "./s25.void.payment.form.component";
import { IPaymentDetails } from "../../s25-payments/payment.service";
import { S25PricingUtil } from "../../s25.pricing.util";
import { S25Util } from "../../../../util/s25-util";
import { S25Datefilter } from "../../../s25-dateformat/s25.datefilter.service";
import { Bind } from "../../../../decorators/bind.decorator";
import { S25IconComponent } from "../../../s25-icon/s25.icon.component";

const DRAFT_ROW_ID = "payment-draft";

const TYPE_OPTIONS = [
    { value: "DEPOSIT", label: "Deposit" },
    { value: "FINAL", label: "Balance" },
    { value: "MISC", label: "Misc" },
];

const STATUS_OPTIONS = [
    { value: "paid", label: "Paid" },
    { value: "unpaid", label: "Unpaid" },
];

const ZERO_BALANCE_WARNING = "The outstanding balance is $0. No payments may be made at this time.";

@TypeManagerDecorator("s25-ng-org-payments")
@Component({
    selector: "s25-ng-org-payments",
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        S25TableModule,
        S25DatepickerModule,
        S25CollapseChevronComponent,
        S25FadingSuccessMsgComponent,
        S25ButtonComponent,
        S25VoidPaymentFormComponent,
        S25IconComponent,
    ],
    template: `
        <div class="payments-header">
            <button
                type="button"
                class="payments-toggle"
                [attr.aria-expanded]="expanded()"
                [attr.aria-controls]="regionId()"
                (click)="expanded.set(!expanded())"
            >
                <s25-ng-collapse-chevron aria-hidden="true" [collapsedOrientation]="'right'" [expanded]="expanded()" />
                <span>Payments</span>
                <span class="payments-count">({{ rowModels().length }})</span>
            </button>

            @if (canSendRequest()) {
                <button type="button" class="c-textButton" (click)="sendRequest()">
                    <s25-ng-icon [type]="'invoice'" [ariaLabel]="'Send Payment Request'" [size]="18" /> Send payment
                    request
                </button>
            }
        </div>

        <div [id]="regionId()">
            @if (expanded()) {
                @if (message()) {
                    <s25-ng-fading-success-msg [(message)]="message" />
                }

                @if (rowModels().length || draftOpen()) {
                    <div class="payments-table">
                        <s25-ng-table
                            [dataSource]="tableConfig()"
                            [unlimitedWidth]="true"
                            [caption]="tableCaption()"
                            [pivotThreshold]="700"
                            [pivotThresholdColumn]="null"
                        />
                    </div>
                } @else {
                    <p class="payments-empty">No payment history found</p>
                }

                @if (errorMessage()) {
                    <p class="payments-error" role="alert">{{ errorMessage() }}</p>
                }

                @if (voidTarget(); as target) {
                    <s25-ng-void-payment-form
                        class="payments-void"
                        [paymentDetailId]="target.paymentDetailId"
                        [amountTotalCents]="target.amountTotalCents ?? 0"
                        (cancelled)="voidTarget.set(undefined)"
                        (voided)="onVoided()"
                    />
                }

                @if (!viewOnly() && !voidTarget()) {
                    <div class="payments-actions">
                        @if (draftOpen()) {
                            <s25-ng-button [type]="'primary'" [onClick]="submitDraft">Submit Payment</s25-ng-button>
                            <s25-ng-button [type]="'outline'" (clicked)="cancelDraft()">Cancel</s25-ng-button>
                        } @else {
                            <s25-ng-button [type]="'outline'" (clicked)="startDraft()">Record payment</s25-ng-button>
                        }
                    </div>
                }
            }
        </div>
    `,
    host: { class: "org-payments" },
    styles: `
        :host {
            display: block;
            border: 1px solid var(--color--table-border);
        }

        .payments-header {
            display: flex;
            align-items: center;
            gap: 1.375rem;
            padding: 0.75rem 1.25rem;
        }

        .payments-header .c-textButton {
            display: flex;
            align-items: center;
            gap: 7px;
        }

        .payments-header .c-textButton s25-ng-icon {
            position: relative;
            bottom: 2px;
        }

        .payments-table {
            padding-inline: 1.25rem;
            padding-block-end: 0.5rem;
        }

        :host ::ng-deep .s25-ng-table--cell s25-datepicker .c-input,
        :host ::ng-deep .s25-ng-table--cell generic-table-simple-select select {
            width: 100%;
            min-width: 0;
            max-width: 100%;
            box-sizing: border-box;
        }

        :host ::ng-deep th[col="paymentId"] {
            width: 4em !important;
        }

        .payments-toggle {
            display: flex;
            align-items: center;
            gap: 0.5rem;
            padding: 0;
            background: none;
            border: none;
            color: var(--color--text);
            font: inherit;
            font-weight: 600;
            cursor: pointer;
        }

        .payments-toggle:focus-visible,
        .c-textButton:focus-visible {
            outline-offset: 4px;
        }

        .payments-count {
            font-weight: 400;
            color: var(--color--text-muted);
        }

        .payments-empty {
            padding: 0 1.25rem 0.75rem;
            color: var(--color--text-muted);
        }

        .payments-error {
            padding: 0 1.25rem 0.5rem;
            color: var(--color--text-danger);
        }

        .payments-actions {
            display: flex;
            gap: 0.5rem;
            padding: 0.75rem 1.25rem;
        }

        .payments-void {
            border-top: 1px solid var(--color--table-border);
        }
    `,
})
export class S25OrgPaymentsComponent {
    protected readonly paymentsStore = inject(PaymentsStore);

    readonly orgId = input.required<number>();
    readonly orgName = input("");

    protected readonly viewOnly = this.paymentsStore.viewOnly;
    protected readonly canSendRequest = this.paymentsStore.canSendRequest;

    protected readonly regionId = computed(() => `org-payments-${this.orgId()}`);
    protected readonly tableCaption = computed(() => `Payments for ${this.orgName() || "this organization"}`);

    protected readonly expanded = signal(false);
    protected readonly message = signal("");
    protected readonly errorMessage = signal("");
    protected readonly voidTarget = signal<IPaymentDetails | undefined>(undefined);

    private readonly table = viewChild(S25TableComponent);

    protected readonly balance = computed(() => this.paymentsStore.remainingBalance(this.orgId()));

    protected readonly rowModels = computed<PaymentRows.PaymentRowModel[]>(() =>
        PaymentRows.paymentRowModels(this.paymentsStore.paymentsByOrg().get(this.orgId()) ?? [], {
            dateFormat: this.paymentsStore.dateFormat(),
            viewOnly: this.viewOnly(),
        }),
    );

    protected readonly draftOpen = computed(() => this.paymentsStore.draftOrgId() === this.orgId());

    // Every draft field restarts from the current balance whenever the draft row is opened or the
    // org's balance moves under it, so a stale amount can never be submitted.
    private readonly draftResetKey = computed(() => `${this.draftOpen()}-${this.balance()}`);

    private readonly draftDueDate = linkedSignal<string, Date>({
        source: this.draftResetKey,
        computation: () => new Date(),
    });
    private readonly draftAmount = linkedSignal<string, PaymentRows.PaymentAmountEntry>({
        source: this.draftResetKey,
        computation: () => ({ value: this.balance(), type: "amount" }),
    });
    private readonly draftType = linkedSignal<string, IPaymentDetails["type"]>({
        source: this.draftResetKey,
        computation: () => "DEPOSIT",
    });
    private readonly draftStatus = linkedSignal<string, "paid" | "unpaid">({
        source: this.draftResetKey,
        computation: () => "unpaid",
    });
    private readonly draftDescription = linkedSignal<string, string>({
        source: this.draftResetKey,
        computation: () => "",
    });
    private readonly draftNotes = linkedSignal<string, string>({
        source: this.draftResetKey,
        computation: () => "",
    });

    protected readonly tableConfig = computed<Table.DataSource>(() => ({
        type: "unpaginated",
        columns: PaymentRows.paymentColumns(),
        dataSource: () => Promise.resolve({ rows: this.buildRows() }),
    }));

    constructor() {
        effect(() => {
            // Re-read rows in place whenever the payments or the draft row change, so a save does
            // not scroll the section away or reset the rest of the table.
            this.rowModels();
            this.draftOpen();
            untracked(() => {
                void this.table()?.refresh(false, false, true);
            });
        });
    }

    protected sendRequest() {
        this.paymentsStore.openRequest(this.orgId());
    }

    protected startDraft() {
        this.errorMessage.set("");
        if (this.balance() <= 0) return this.errorMessage.set(ZERO_BALANCE_WARNING);

        this.expanded.set(true);
        this.paymentsStore.openDraft(this.orgId());
    }

    protected cancelDraft() {
        this.errorMessage.set("");
        this.paymentsStore.closeDraft();
    }

    protected onVoided() {
        this.voidTarget.set(undefined);
        this.message.set("Payment voided");
    }

    @Bind
    protected async submitDraft() {
        const amount = PaymentRows.paymentAmountDollars(this.draftAmount(), this.balance());
        if (amount <= 0) return this.errorMessage.set("Enter an amount greater than $0.");
        if (amount > this.balance()) {
            return this.errorMessage.set(
                `Cannot exceed the outstanding balance of ${S25PricingUtil.formatCurrency(this.balance())}.`,
            );
        }

        this.errorMessage.set("");

        const { error } = await this.paymentsStore.createPayment({
            productDescription: this.draftDescription(),
            amountInCents: S25PricingUtil.convertToCents(amount),
            organizationId: this.orgId(),
            type: this.draftType(),
            source: "manual",
            dueDate: S25Util.date.toS25ISODateStrStartOfDay(this.draftDueDate()),
            paymentStatus: this.draftStatus(),
            notes: this.draftNotes(),
        });

        if (error) return this.errorMessage.set(error);

        this.paymentsStore.closeDraft();
        this.message.set("Payment recorded");
    }

    private buildRows(): Table.Row[] {
        const rows = this.rowModels().map((model) => this.historyRow(model));
        return this.draftOpen() ? [...rows, this.draftRow()] : rows;
    }

    private historyRow(model: PaymentRows.PaymentRowModel): Table.Row {
        return {
            id: model.rowId,
            name: `Payment ${model.paymentDetailId}`,
            className: model.isVoid ? "payment-row--void" : "",
            cells: {
                paymentId: { text: model.paymentDetailId },
                created: { text: model.createdDate },
                due: { text: model.dueDate },
                source: { text: model.source },
                description: { text: model.description },
                amount: { text: model.amount },
                type: this.actionCell({
                    text: model.typeLabel,
                    actionLabel: model.canRemind && this.canSendRequest() ? "Send Reminder" : "",
                    onAction: () => this.paymentsStore.openReminder(this.orgId(), model.paymentDetailId),
                }),
                status: this.actionCell({
                    text: model.canMarkPaid ? "" : model.statusText,
                    actionLabel: model.canMarkPaid ? "Mark as Paid" : "",
                    onAction: () => this.markPaid(model.paymentDetailId),
                }),
                void: this.actionCell({
                    text: model.isVoid ? model.voidText : model.canVoid ? "" : "Cannot Void",
                    actionLabel: model.canVoid ? "Void" : "",
                    actionType: "danger",
                    onAction: () => this.voidTarget.set(model.payment),
                }),
                notes: {
                    component: S25PaymentNotesCellComponent,
                    inputs: {
                        paymentDetailId: model.paymentDetailId,
                        notes: model.notes,
                        readOnly: this.viewOnly() || model.isVoid,
                    },
                    textValue: model.notes,
                } satisfies Table.Cell<S25PaymentNotesCellComponent>,
            },
        };
    }

    private actionCell(args: {
        text: string | number;
        actionLabel: string;
        actionType?: "outline" | "primary" | "danger";
        onAction: () => Promise<void> | void;
    }): Table.Cell {
        return {
            component: S25PaymentActionCellComponent,
            inputs: {
                text: String(args.text),
                actionLabel: args.actionLabel,
                actionType: args.actionType ?? "outline",
                onAction: args.onAction,
            },
            textValue: String(args.text),
        } satisfies Table.Cell<S25PaymentActionCellComponent>;
    }

    private async markPaid(paymentDetailId: number) {
        const { error } = await this.paymentsStore.markPaid(paymentDetailId);
        if (error) return this.errorMessage.set(error);

        this.errorMessage.set("");
        this.message.set("Payment marked as paid");
    }

    private draftRow(): Table.Row {
        return {
            id: DRAFT_ROW_ID,
            name: "New payment",
            className: "payment-row--draft",
            cells: {
                paymentId: { text: "" },
                created: { text: S25Datefilter.transform(new Date(), this.paymentsStore.dateFormat() ?? "") ?? "" },
                due: {
                    component: S25DatepickerComponent,
                    inputs: { value: this.draftDueDate(), ariaLabel: "Payment due date" },
                    outputs: {
                        valueChange: (value) => {
                            if (value) this.draftDueDate.set(new Date(value));
                        },
                    },
                } satisfies Table.Cell<S25DatepickerComponent>,
                source: { text: "manual" },
                description: {
                    component: S25PaymentTextFieldComponent,
                    inputs: { value: this.draftDescription(), label: "Payment description" },
                    outputs: { valueChange: (value) => this.draftDescription.set(value) },
                } satisfies Table.Cell<S25PaymentTextFieldComponent>,
                amount: {
                    component: S25PaymentAmountFieldComponent,
                    inputs: {
                        entry: this.draftAmount(),
                        balance: this.balance(),
                        groupName: `payment-amount-${this.orgId()}`,
                    },
                    outputs: { entryChange: (entry) => this.draftAmount.set(entry) },
                } satisfies Table.Cell<S25PaymentAmountFieldComponent>,
                type: {
                    component: GenericTableSimpleSelectComponent,
                    inputs: { modelValue: this.draftType(), options: TYPE_OPTIONS, ariaLabel: "Payment type" },
                    outputs: {
                        modelValueChange: (value) => this.draftType.set(value as IPaymentDetails["type"]),
                    },
                } satisfies Table.Cell<GenericTableSimpleSelectComponent>,
                status: {
                    component: GenericTableSimpleSelectComponent,
                    inputs: { modelValue: this.draftStatus(), options: STATUS_OPTIONS, ariaLabel: "Payment status" },
                    outputs: { modelValueChange: (value) => this.draftStatus.set(value as "paid" | "unpaid") },
                } satisfies Table.Cell<GenericTableSimpleSelectComponent>,
                void: { text: "N/A" },
                notes: {
                    component: S25PaymentTextFieldComponent,
                    inputs: { value: this.draftNotes(), label: "Payment notes" },
                    outputs: { valueChange: (value) => this.draftNotes.set(value) },
                } satisfies Table.Cell<S25PaymentTextFieldComponent>,
            },
        };
    }
}
